import { InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'chakra-react-select';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { CompanyDraftContent } from '@blockpulse3/graphql/hooks';
import { alpha3CountryOptions } from '@blockpulse3/helpers';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { companyDraftContentDefaultValues, schemaLegal } from '../../provider/schema';

type Props = {
  defaultValues?: CompanyDraftContent;
  onSubmit: (data: CompanyDraftContent) => void;
};

/**
 * LegalInformationsForm.
 * Form for the legal informations of a SPV.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function LegalInformationsForm({
  defaultValues = companyDraftContentDefaultValues,
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nLegalInformationsForm = useTranslations('LegalInformationsForm');

  const { register, control, formState, handleSubmit } = useForm<CompanyDraftContent>({
    defaultValues,
    resolver: yupResolver(schemaLegal),
  });

  const handleFormSubmit: SubmitHandler<CompanyDraftContent> = (data) => {
    onSubmit(data);
  };

  return (
    <>
      <CardHeader>
        <Heading size="lg">{i18nLegalInformationsForm('FormTitle')}</Heading>
      </CardHeader>
      <Divider />
      <CardBody>
        <form id="legal-informations" onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing="4">
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Stack spacing="4">
                <Text fontSize="lg" fontWeight="bold">
                  {i18nLegalInformationsForm('SectionTitle1')}
                </Text>
                <FormControl isInvalid={!!formState.errors?.name}>
                  <FormLabel htmlFor="name">{i18nLegalInformationsForm('NameField')}</FormLabel>
                  <Input id="name" type="text" {...register('name')} />
                  <ErrorMessage error={formState.errors.name} />
                </FormControl>
                <Stack spacing="4">
                  <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                    <FormControl isInvalid={!!formState.errors?.address?.line}>
                      <FormLabel htmlFor="line">
                        {i18nLegalInformationsForm('Address.LineField')}
                      </FormLabel>
                      <Input id="line" type="string" {...register('address.line')} />
                      <ErrorMessage error={formState.errors.address?.line} />
                    </FormControl>
                    <FormControl isInvalid={!!formState.errors?.address?.city}>
                      <FormLabel htmlFor="city">
                        {i18nLegalInformationsForm('Address.CityField')}
                      </FormLabel>
                      <Input id="city" type="string" {...register('address.city')} />
                      <ErrorMessage error={formState.errors.address?.city} />
                    </FormControl>
                  </Stack>
                  <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                    <FormControl isInvalid={!!formState.errors?.address?.postalCode}>
                      <FormLabel htmlFor="zipcode">
                        {i18nLegalInformationsForm('Address.ZipCodeField')}
                      </FormLabel>
                      <Input id="zipcode" type="string" {...register('address.postalCode')} />
                      <ErrorMessage error={formState.errors.address?.postalCode} />
                    </FormControl>
                    <Controller
                      control={control}
                      name="address.countryData"
                      render={({ field }): JSX.Element => (
                        <FormControl isInvalid={!!formState.errors.address?.countryData}>
                          <FormLabel htmlFor="country">
                            {i18nLegalInformationsForm('Address.CountryField')}
                          </FormLabel>
                          <Select
                            id="country"
                            menuPlacement="auto"
                            options={alpha3CountryOptions}
                            {...field}
                          />
                          <ErrorMessage error={formState.errors.address?.countryData?.value} />
                        </FormControl>
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack bg="gray.50" borderRadius="md" p="3" spacing="2">
              <Stack spacing="4">
                <Text fontSize="lg" fontWeight="bold">
                  {i18nLegalInformationsForm('SectionTitle2')}
                </Text>
                <FormControl isInvalid={!!formState.errors?.target?.name}>
                  <FormLabel htmlFor="targetName">
                    {i18nLegalInformationsForm('DealField')}
                  </FormLabel>
                  <Input id="targetName" type="text" {...register('target.name')} />
                  <ErrorMessage error={formState.errors.target?.name} />
                </FormControl>
                <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
                  <FormControl isInvalid={!!formState.errors?.target?.registrationNumber}>
                    <HStack alignItems="flex-start" spacing="0">
                      <FormLabel htmlFor="registrationNumber">
                        {i18nLegalInformationsForm('RegistrationNumber')}
                      </FormLabel>
                      <Tooltip
                        hasArrow
                        label={i18nLegalInformationsForm('Tooltip.IdentificationNumberField')}
                        placement="top"
                      >
                        <Icon as={InfoIcon} color="gray.500" />
                      </Tooltip>
                    </HStack>
                    <Input
                      id="registrationNumber"
                      type="text"
                      {...register('target.registrationNumber')}
                    />
                    <ErrorMessage error={formState.errors.target?.registrationNumber} />
                  </FormControl>
                  <FormControl isInvalid={!!formState.errors?.target?.registrationEntity}>
                    <FormLabel htmlFor="registrationEntity">
                      {i18nLegalInformationsForm('RegistrationEntity')}
                    </FormLabel>
                    <Input
                      id="registrationEntity"
                      type="text"
                      {...register('target.registrationEntity')}
                    />
                    <ErrorMessage error={formState.errors.target?.registrationEntity} />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </form>
      </CardBody>
      <CardFooter justifyContent="flex-end">
        <Button form="legal-informations" type="submit" w="full">
          {i18nGeneral('Next')}
        </Button>
      </CardFooter>
    </>
  );
}

export type LegalInformationsFormProps = Props;
